import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Mail,
  Phone,
  ArrowRight,
  TrendingUp,
  ArrowLeft,
  Shield,
} from "lucide-react";
import { toast } from "react-toastify";
import authService from "../../components/services/AuthService";

const ForgotPassword = () => {
  const [resetType, setResetType] = useState("email");
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object({
    identifier:
      resetType === "email"
        ? Yup.string()
            .email("Please enter a valid email address")
            .required("Email address is required")
            .trim()
        : Yup.string()
            .test(
              "phone-format",
              "Invalid phone number format",
              function (value) {
                if (!value) return false;
                const cleanValue = value.replace(/[\s\-()]/g, "");
                
                // Format: +233XXXXXXXXX (13 digits)
                if (cleanValue.startsWith("+233")) {
                  return (
                    cleanValue.length === 13 &&
                    /^\+233[0-9]\d{8}$/.test(cleanValue)
                  );
                }
                
                // Format: 233XXXXXXXXX (12 digits)
                if (cleanValue.startsWith("233")) {
                  return (
                    cleanValue.length === 12 &&
                    /^233[0-9]\d{8}$/.test(cleanValue)
                  );
                }
                
                // Format: 0XXXXXXXXX (10 digits)
                if (cleanValue.startsWith("0")) {
                  return (
                    cleanValue.length === 10 && /^0[0-9]\d{8}$/.test(cleanValue)
                  );
                }
                
                // Format: XXXXXXXXX (9 digits) - without country code or leading 0
                if (/^[0-9]/.test(cleanValue)) {
                  return (
                    cleanValue.length === 9 && /^[0-9]\d{8}$/.test(cleanValue)
                  );
                }
                
                return false;
              }
            )
            .required("Phone number is required"),
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const payload = {
        emailOrPhone: values.identifier,
      };
      const response = await authService.forgotPassword(payload);
      if (response.data?.status_code === "000") {
        toast.success(response.data?.reason);
        navigate("/verify-otp", { state: { userData: response.data?.data } });
      } else {
        toast.error(response.data?.reason);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.reason ||
          "Failed to send reset code. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      {/* Left Side */}
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background animations */}
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Floating stats cards */}
        <motion.div
          className="absolute top-10 right-10 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Secure Reset</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-10 bottom-20 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">24/7 Support</span>
          </div>
        </motion.div>

        {/* Main image container */}
        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="overflow-hidden relative w-full h-full rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop&crop=center"
              alt="dealboxx-password-reset"
              className="object-cover w-full h-full rounded-2xl transition-transform duration-700 hover:scale-110"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
            />

            {/* Content overlay */}
            <motion.div
              className="flex absolute inset-0 flex-col justify-end items-center pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div
                className="px-8 text-center"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                <motion.h2
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Password Reset
                </motion.h2>
                <motion.p
                  className="mx-auto mb-8 max-w-md text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Secure and quick password recovery process
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Right Side */}
      <motion.div
        className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-10 h-10 bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
              Dealboxx
            </span>
          </div>
        </motion.div>

        {/* Welcome Text */}
        <motion.div
          className="mb-6 space-y-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Forgot Password?
          </h1>
          <p className="text-[#808080] text-base">
            No worries! Enter your details and we'll send you reset code
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={{ identifier: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <Form className="space-y-6">
                {/* Reset Type Toggle */}
                <div className="flex p-1 bg-gray-100 rounded-2xl">
                  <button
                    type="button"
                    onClick={() => setResetType("email")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl transition-all duration-300 ${
                      resetType === "email"
                        ? "bg-white text-gray-900 shadow-lg"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setResetType("phone")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl transition-all duration-300 ${
                      resetType === "phone"
                        ? "bg-white text-gray-900 shadow-lg"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Phone className="w-4 h-4" />
                    Phone
                  </button>
                </div>

                {/* Identifier Field */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {resetType === "email" ? "Email Address" : "Phone Number"}
                  </label>
                  <div className="relative">
                    <Field
                      name="identifier"
                      type={resetType === "email" ? "email" : "tel"}
                      placeholder={
                        resetType === "email"
                          ? "e.g., john.doe@example.com"
                          : "e.g., +233 24 123 4567 or 024 123 4567"
                      }
                      className="px-4 py-4 w-full placeholder-gray-400 text-gray-900 rounded-2xl border transition-all duration-300 bg-white border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent hover:border-gray-300"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {resetType === "email" ? (
                        <Mail className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Phone className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {resetType === "phone" && (
                    <div className="mt-1 text-xs text-gray-500">
                      Format: +233 XX XXX XXXX or 0XX XXX XXXX (Ghana numbers
                      only)
                    </div>
                  )}
                  {resetType === "email" && (
                    <div className="mt-1 text-xs text-gray-500">
                      Enter your registered email address
                    </div>
                  )}
                  <ErrorMessage
                    name="identifier"
                    component="div"
                    className="mt-1 text-sm text-red-500"
                  />
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex gap-2 justify-center items-center px-6 py-4 w-full font-semibold text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                      Sending Reset Code...
                    </>
                  ) : (
                    <>
                      Send Reset Code
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </motion.button>
              </Form>
            </motion.div>
          )}
        </Formik>

        {/* Security Features */}
        <motion.div
          className="grid grid-cols-3 gap-3 mt-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Secure Process</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">⚡</div>
              <div className="text-xs text-gray-600">Quick Recovery</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">📱</div>
              <div className="text-xs text-gray-600">Multi-Channel</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Security Info Section */}
        <motion.div
          className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9B9B]/10 border border-[#FF6C6C]/20"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="flex gap-2 items-center mb-2">
            <Shield className="w-4 h-4 text-[#FF6C6C]" />
            <span className="text-sm font-semibold text-[#FF6C6C]">
              Security Features
            </span>
          </div>
          <div className="space-y-1 text-xs text-gray-600">
            <div>• Encrypted reset links & OTP codes</div>
            <div>• Time-limited reset tokens (15 minutes)</div>
            <div>• Multi-factor authentication support</div>
          </div>
        </motion.div>

        {/* Back to Login */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 text-center"
        >
          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm text-gray-500 transition-colors hover:text-[#FF6C6C]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
